import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Clock,
  LogOut,
  Mail,
  PackageSearch,
} from "lucide-react";
import { getKits, logout, type Kit } from "@/services/kits";
import { startShopifyLogin } from "@/services/shopify";
import { GradientMesh } from "@/components/gradient-mesh";

const PRODUCT_IMAGE = "/cc-mobile-product.png";

type LoadState =
  | { phase: "loading" }
  | { phase: "signed_out" }
  | { phase: "ready"; kits: Kit[] }
  | { phase: "error" };

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-dvh flex-col items-center overflow-hidden bg-surface px-5 py-10 sm:px-6 sm:py-14">
      <GradientMesh intensity="subtle" />
      <div className="relative z-10 w-full max-w-lg">{children}</div>
    </div>
  );
}

function Spinner() {
  return (
    <div
      className="cc-prepare-spinner size-10 rounded-full border-[2.5px] border-slate-200/90 border-t-navy"
      aria-hidden
    />
  );
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function SignIn({ failed }: { failed: boolean }) {
  return (
    <div className="flex min-h-[80dvh] flex-col items-center justify-center text-center">
      <img src="/logo.png" alt="CheckCells" className="mb-7 size-12" />

      <img
        src={PRODUCT_IMAGE}
        alt="CheckCells kit"
        className="mb-8 w-full max-w-xs select-none"
        draggable={false}
      />

      <h1 className="text-2xl font-bold tracking-tight text-navy sm:text-3xl">
        Track your CheckCells kit
      </h1>
      <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-muted">
        Sign in with the same email you used to place your order to see your
        kits and view results when they're ready.
      </p>

      {failed && (
        <p className="mx-auto mt-6 max-w-sm rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
          We couldn't complete your sign-in. Please try again.
        </p>
      )}

      <button
        type="button"
        onClick={startShopifyLogin}
        className="mt-8 inline-flex min-h-[52px] w-full max-w-sm items-center justify-center rounded-full bg-navy px-6 text-sm font-semibold text-white shadow-lg shadow-navy/20 transition hover:bg-navy-hover"
      >
        Sign in with Shopify
      </button>
    </div>
  );
}

function StatusBadge({ status }: { status: Kit["status"] }) {
  if (status === "ready") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green/20 px-2.5 py-1 text-xs font-semibold text-navy">
        <CheckCircle2 className="size-3.5" aria-hidden />
        Results ready
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">
      <Clock className="size-3.5" aria-hidden />
      Processing
    </span>
  );
}

function KitCard({ kit }: { kit: Kit }) {
  const navigate = useNavigate();
  const ready = kit.status === "ready" && kit.token;
  const date = formatDate(kit.created_at);
  const title = kit.items[0] ?? "CheckCells Kit";

  const body = (
    <>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-semibold text-navy">{title}</p>
        </div>
        <p className="mt-0.5 text-xs text-slate-400">
          Order {kit.order_name}
          {date ? ` · ${date}` : ""}
        </p>
        <div className="mt-2">
          <StatusBadge status={kit.status} />
        </div>
      </div>
      {ready && <ChevronRight className="size-5 shrink-0 self-center text-slate-300" aria-hidden />}
    </>
  );

  if (ready) {
    return (
      <button
        type="button"
        onClick={() => navigate(`/r/${kit.token}`)}
        className="flex w-full items-start gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 text-left shadow-sm transition hover:border-green hover:shadow-md"
      >
        {body}
      </button>
    );
  }

  return (
    <div className="flex w-full items-start gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
      {body}
    </div>
  );
}

function Dashboard({ kits, onSignOut }: { kits: Kit[]; onSignOut: () => void }) {
  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <img src="/logo.png" alt="CheckCells" className="size-10" />
        <button
          type="button"
          onClick={onSignOut}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-slate-500 transition hover:bg-slate-100 hover:text-navy"
        >
          <LogOut className="size-4" aria-hidden />
          Sign out
        </button>
      </header>

      <h1 className="text-xl font-bold tracking-tight text-navy sm:text-2xl">
        Your kits
      </h1>

      {kits.length > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-green/10 px-3.5 py-3 text-xs leading-relaxed text-navy">
          <Mail className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            We'll email you as soon as a kit's results are ready. Tap any kit
            with ready results to view them.
          </span>
        </div>
      )}

      {kits.length === 0 ? (
        <div className="mt-10 flex flex-col items-center text-center">
          <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-slate-100">
            <PackageSearch className="size-8 text-slate-500" aria-hidden />
          </div>
          <h2 className="text-base font-semibold text-navy">No kits found</h2>
          <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-muted">
            We couldn't find any kit orders under this account. Make sure you
            signed in with the email you used at checkout.
          </p>
          <a
            href="https://checkcells.com"
            className="mt-5 text-sm font-medium text-navy underline underline-offset-2"
          >
            Order a kit at checkcells.com
          </a>
        </div>
      ) : (
        <div className="mt-5 flex flex-col gap-3">
          {kits.map((kit, i) => (
            <KitCard key={`${kit.order_name}-${i}`} kit={kit} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function KitPage() {
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState<LoadState>({ phase: "loading" });

  const load = useCallback(async () => {
    setState({ phase: "loading" });
    try {
      const kits = await getKits();
      setState(kits === null ? { phase: "signed_out" } : { phase: "ready", kits });
    } catch {
      setState({ phase: "error" });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSignOut = useCallback(() => {
    // Navigates away (clears our session + ends the Shopify session).
    logout();
  }, []);

  const signInFailed = params.get("error") === "signin";

  // Drop the ?error param from the URL once we've read it.
  useEffect(() => {
    if (signInFailed) {
      params.delete("error");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.phase === "loading") {
    return (
      <Shell>
        <div className="flex min-h-[60dvh] items-center justify-center">
          <Spinner />
        </div>
      </Shell>
    );
  }

  if (state.phase === "error") {
    return (
      <Shell>
        <div className="flex min-h-[60dvh] flex-col items-center justify-center text-center">
          <AlertCircle className="mb-4 size-10 text-red-500" aria-hidden />
          <p className="text-sm text-muted">Something went wrong loading your kits.</p>
          <button
            type="button"
            onClick={load}
            className="mt-5 min-h-[44px] rounded-xl bg-navy px-5 text-sm font-semibold text-white transition hover:bg-navy-hover"
          >
            Try again
          </button>
        </div>
      </Shell>
    );
  }

  if (state.phase === "signed_out") {
    return (
      <Shell>
        <SignIn failed={signInFailed} />
      </Shell>
    );
  }

  return (
    <Shell>
      <Dashboard kits={state.kits} onSignOut={handleSignOut} />
    </Shell>
  );
}
